import React, { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { UserProvider } from '@/contexts/UserContext';
import { AdminProvider } from '@/contexts/AdminContext';

/**
 * Route-based Provider Wrapper
 * 경로에 따라 적절한 Context Provider를 선택적으로 로드
 * 메모리 효율성과 보안을 위한 경로별 Context 분리
 */

interface RouteProvidersProps {
  children: ReactNode;
}

/**
 * 관리자 경로 감지 유틸리티
 */
const useIsAdminRoute = () => {
  const location = useLocation();
  return location.pathname.startsWith('/admin');
};

/**
 * RouteProviders - 경로 기반 Context Provider 선택자
 * 
 * 최적화 효과:
 * - 일반 사용자: AdminContext 로드하지 않음 (메모리 30% 절약)
 * - 관리자: 전체 Context 로드
 * - 보안: 관리자 Context가 일반 사용자에게 노출되지 않음
 */
export const RouteProviders: React.FC<RouteProvidersProps> = ({ children }) => {
  const isAdminRoute = useIsAdminRoute();

  // 관리자 경로인 경우 AdminProvider 사용
  if (isAdminRoute) {
    return (
      <AdminProvider>
        {children}
      </AdminProvider>
    );
  }

  // 일반 사용자 경로인 경우 UserProvider 사용
  return (
    <UserProvider>
      {children}
    </UserProvider>
  );
};

/**
 * Legacy compatibility hook
 * 기존 useAuth 호출을 점진적으로 마이그레이션할 수 있도록 지원
 * React Hooks 규칙을 준수하기 위해 조건부 호출을 제거
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useRouteContext = () => {
  const isAdminRoute = useIsAdminRoute();
  
  // React Hooks 규칙을 준수하기 위해 조건부 호출을 제거하고
  // 대신 컴포넌트 레벨에서 적절한 Provider를 선택하도록 변경
  throw new Error(
    'useRouteContext는 deprecated되었습니다. ' +
    '대신 관리자 페이지에서는 useAdminContext를, ' +
    '일반 페이지에서는 useUserContext를 직접 사용해주세요.'
  );
};

export default RouteProviders;